/**
 * Smart Builder — Trace's AI Analytics Assistant.
 *
 * Users ask questions in plain English (e.g. "What was the top performing
 * channel when Osmo launched?"). The backend routes through Gemini 2.5 Flash,
 * which invokes deterministic Python analytics tools against Mongo, and
 * composes a concise answer + evidence panel.
 *
 * The UI is intentionally chat-shaped but scoped to the Exploration page:
 * a single stacked feed with a sticky composer at the bottom, plus a small
 * "Based on" strip under each answer showing which tools were called.
 */
import { useState, useRef, useEffect } from 'react';
import { Sparkles, Send, RotateCcw, ChevronDown, ChevronRight, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const EXAMPLES = [
  'What was the top performing channel when Osmo launched?',
  'Which channel had the best ROAS last month?',
  'Compare Meta and Google for Osmo.',
  'Which product grew the most in the last 3 months?',
  'Which channel generated the most revenue?',
];

// Simple session-id per browser tab. Persist so refresh keeps context.
const sessionId = (() => {
  try {
    const k = 'trace_smart_builder_sid';
    let v = localStorage.getItem(k);
    if (!v) { v = 'sb-' + Math.random().toString(36).slice(2, 10) + '-' + Date.now(); localStorage.setItem(k, v); }
    return v;
  } catch { return 'sb-' + Date.now(); }
})();

export default function SmartBuilder() {
  const [turns, setTurns] = useState([]); // [{role, content, tools_used}]
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const feedRef = useRef(null);

  // Restore prior conversation on first mount so users pick up where they left off.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/smart-builder/session/${sessionId}`, { credentials: 'include' });
        if (!r.ok) return;
        const d = await r.json();
        if (cancelled) return;
        setTurns((d.turns || []).map(t => ({ role: t.role, content: t.content, tools_used: t.tools_used || [] })));
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [turns, busy]);

  const ask = async (q) => {
    const question = (q ?? input).trim();
    if (!question || busy) return;
    setInput('');
    setTurns(prev => [...prev, { role: 'user', content: question }]);
    setBusy(true);
    try {
      const r = await fetch(`${API}/smart-builder/ask`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId, question }),
      });
      if (!r.ok) {
        const t = await r.text();
        setTurns(prev => [...prev, { role: 'assistant', content: `Sorry — the assistant hit an error (${r.status}). ${t.slice(0, 200)}` }]);
      } else {
        const d = await r.json();
        setTurns(prev => [...prev, { role: 'assistant', content: d.answer || '—', tools_used: d.tools_used || [] }]);
      }
    } catch (e) {
      setTurns(prev => [...prev, { role: 'assistant', content: `Sorry — could not reach the assistant. ${String(e)}` }]);
    } finally {
      setBusy(false);
    }
  };

  const reset = async () => {
    setTurns([]);
    setExpanded({});
    try {
      await fetch(`${API}/smart-builder/session/${sessionId}`, { method: 'DELETE', credentials: 'include' });
    } catch { /* ignore */ }
  };

  return (
    <div className="mt-4 bg-gradient-to-br from-purple-50/60 to-white border border-purple-100 rounded-xl p-4" data-testid="smart-builder">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-[#0F0F0F] flex items-center gap-2">
            Smart Builder
            <span className="text-[10px] font-semibold uppercase tracking-wider text-purple-600 bg-purple-100 rounded-full px-2 py-0.5">Beta</span>
          </div>
          <div className="text-[11px] text-gray-500 mt-0.5">Ask anything about your marketing performance — powered by Gemini + Trace analytics tools.</div>
        </div>
        {turns.length > 0 && (
          <button onClick={reset} className="text-[11px] text-gray-500 hover:text-gray-800 flex items-center gap-1 px-2 py-1 rounded hover:bg-gray-100" data-testid="smart-builder-reset">
            <RotateCcw className="w-3.5 h-3.5" /> New chat
          </button>
        )}
      </div>

      {/* Feed */}
      <div ref={feedRef} className="max-h-[440px] overflow-y-auto space-y-3 pr-1" data-testid="smart-builder-feed">
        {turns.length === 0 && !busy && (
          <div className="text-center py-5">
            <p className="text-xs text-gray-500 mb-2">Try one of these:</p>
            <div className="flex flex-wrap gap-2 justify-center">
              {EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => ask(ex)}
                  className="text-[11px] text-purple-700 bg-white border border-purple-200 rounded-full px-3 py-1.5 hover:bg-purple-50 hover:border-purple-300 transition-colors"
                  data-testid={`smart-builder-example-${i}`}
                >
                  {ex}
                </button>
              ))}
            </div>
          </div>
        )}

        {turns.map((t, i) => (
          <div key={i} className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {t.role === 'user' ? (
              <div className="max-w-[85%] bg-[#0F0F0F] text-white text-sm rounded-2xl rounded-br-md px-3.5 py-2 whitespace-pre-wrap" data-testid={`smart-builder-user-${i}`}>
                {t.content}
              </div>
            ) : (
              <div className="max-w-[92%] bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3.5 py-2.5" data-testid={`smart-builder-assistant-${i}`}>
                <AssistantMarkdown content={t.content} />
                {t.tools_used && t.tools_used.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => setExpanded(p => ({ ...p, [i]: !p[i] }))}
                      className="flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500 hover:text-gray-800"
                      data-testid={`smart-builder-based-on-${i}`}
                    >
                      {expanded[i] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      Based on · {t.tools_used.length} tool call{t.tools_used.length === 1 ? '' : 's'}
                    </button>
                    {expanded[i] && (
                      <div className="mt-1.5 space-y-1">
                        {t.tools_used.map((tu, j) => (
                          <div key={j} className="text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-md p-2">
                            <div className="font-mono text-[10.5px] text-[#0F766E]">{tu.name}({fmtArgs(tu.args)})</div>
                            {tu.result_preview && <div className="text-[10.5px] text-gray-500 mt-0.5 font-mono truncate" title={tu.result_preview}>→ {tu.result_preview}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {busy && (
          <div className="flex justify-start">
            <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-md px-3.5 py-2.5 text-xs text-gray-500 flex items-center gap-2" data-testid="smart-builder-thinking">
              <Loader2 className="w-3.5 h-3.5 animate-spin text-purple-600" />
              Thinking… (calling Trace analytics tools)
            </div>
          </div>
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => { e.preventDefault(); ask(); }}
        className="mt-3 flex items-center gap-2 bg-white border border-gray-200 rounded-full pl-4 pr-1 py-1 focus-within:border-purple-400 focus-within:ring-2 focus-within:ring-purple-100"
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={busy}
          placeholder="Ask anything about your marketing performance..."
          className="flex-1 text-sm bg-transparent focus:outline-none placeholder:text-gray-400"
          data-testid="smart-builder-input"
        />
        <button
          type="submit"
          disabled={busy || !input.trim()}
          className="w-8 h-8 rounded-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-200 disabled:text-gray-400 text-white flex items-center justify-center shrink-0 transition-colors"
          data-testid="smart-builder-send"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      </form>
      <p className="text-[10px] text-gray-400 mt-1.5 text-center">Trace AI never invents metrics — every number comes from your uploaded data.</p>
    </div>
  );
}

function fmtArgs(a) {
  if (!a) return '';
  try {
    return Object.entries(a).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join(', ');
  } catch { return ''; }
}

// Lightweight markdown for the assistant bubble — supports **bold**, _italic_
// and preserves line breaks. Kept intentionally minimal to avoid pulling in a
// full markdown library for a couple of formatting cues.
function AssistantMarkdown({ content }) {
  const parts = String(content || '').split(/(\*\*[^*]+\*\*|_[^_]+_)/g);
  return (
    <div className="text-sm text-[#0F0F0F] whitespace-pre-wrap leading-relaxed">
      {parts.map((p, i) => {
        if (p.startsWith('**') && p.endsWith('**')) return <strong key={i} className="font-semibold text-[#0F0F0F]">{p.slice(2, -2)}</strong>;
        if (p.startsWith('_') && p.endsWith('_') && p.length > 2) return <em key={i} className="text-gray-500 not-italic text-[11.5px]">{p.slice(1, -1)}</em>;
        return <span key={i}>{p}</span>;
      })}
    </div>
  );
}
